import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Save, Trash2, FileText, Wrench, GitBranch, Globe, FolderOpen, Plus, X,
} from 'lucide-react';
import { useSkill, useUpdateSkill, useDeleteSkill } from '../hooks/useSkills';
import { SkillType, SkillScope, type SkillVariable } from '../types/skill';

const TYPE_LABELS: Record<string, string> = {
  [SkillType.PROMPT_TEMPLATE]: 'Prompt Template',
  [SkillType.TOOL_CONFIG]: 'Tool Config',
  [SkillType.WORKFLOW]: 'Workflow',
};

const TYPE_ICONS: Record<string, typeof FileText> = {
  [SkillType.PROMPT_TEMPLATE]: FileText,
  [SkillType.TOOL_CONFIG]: Wrench,
  [SkillType.WORKFLOW]: GitBranch,
};

export default function SkillDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: skill, isLoading } = useSkill(id);
  const updateSkill = useUpdateSkill();
  const deleteSkill = useDeleteSkill();

  const [editing, setEditing] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [content, setContent] = useState('');
  const [variables, setVariables] = useState<SkillVariable[]>([]);
  const [activeTab, setActiveTab] = useState<'content' | 'variables' | 'config'>('content');

  const startEdit = () => {
    if (!skill) return;
    setName(skill.name);
    setDescription(skill.description || '');
    setContent(skill.content || '');
    setVariables(skill.variables || []);
    setEditing(true);
  };

  const handleSave = async () => {
    if (!id) return;
    await updateSkill.mutateAsync({
      id,
      name: name || undefined,
      description: description || null,
      content: content || null,
      variables: variables.length > 0 ? variables : null,
    });
    setEditing(false);
  };

  const handleDelete = async () => {
    if (!id) return;
    await deleteSkill.mutateAsync(id);
    navigate(-1);
  };

  const addVariable = () => {
    setVariables([...variables, { name: '', description: '', required: false }]);
  };

  const updateVariable = (idx: number, field: keyof SkillVariable, value: string | boolean) => {
    const updated = [...variables];
    (updated[idx] as unknown as Record<string, string | boolean>)[field] = value;
    setVariables(updated);
  };

  const removeVariable = (idx: number) => {
    setVariables(variables.filter((_, i) => i !== idx));
  };

  if (isLoading) {
    return <div className="flex h-64 items-center justify-center text-sm text-slate-400">Loading...</div>;
  }

  if (!skill) {
    return <div className="flex h-64 items-center justify-center text-sm text-slate-500">Skill not found</div>;
  }

  const TypeIcon = TYPE_ICONS[skill.type] || FileText;
  const ScopeIcon = skill.scope === SkillScope.GLOBAL ? Globe : FolderOpen;

  return (
    <div className="mx-auto max-w-4xl p-6">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate(-1)}
          className="mb-3 inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          Back
        </button>
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-slate-100 p-2.5">
              <TypeIcon className="h-5 w-5 text-slate-600" />
            </div>
            <div>
              {editing ? (
                <input
                  type="text"
                  value={name}
                  onChange={e => setName(e.target.value)}
                  className="rounded border border-slate-300 px-2 py-1 text-lg font-semibold focus:border-slate-500 focus:outline-none"
                />
              ) : (
                <h1 className="text-lg font-semibold text-slate-900">{skill.name}</h1>
              )}
              <div className="mt-1 flex items-center gap-2">
                <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600">
                  {TYPE_LABELS[skill.type]}
                </span>
                <span className="inline-flex items-center gap-1 text-xs text-slate-400">
                  <ScopeIcon className="h-3 w-3" />
                  {skill.scope === SkillScope.GLOBAL ? 'Global' : 'Project'}
                </span>
              </div>
            </div>
          </div>
          <div className="flex gap-2">
            {editing ? (
              <>
                <button
                  onClick={() => setEditing(false)}
                  className="rounded-md border border-slate-300 px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={updateSkill.isPending}
                  className="inline-flex items-center gap-1.5 rounded-md bg-slate-900 px-3 py-1.5 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-50"
                >
                  <Save className="h-3.5 w-3.5" />
                  {updateSkill.isPending ? 'Saving...' : 'Save'}
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={startEdit}
                  className="rounded-md border border-slate-300 px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-50"
                >
                  Edit
                </button>
                <button
                  onClick={handleDelete}
                  className="rounded-md border border-red-200 px-3 py-1.5 text-sm text-red-600 hover:bg-red-50"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Description */}
      {editing ? (
        <textarea
          value={description}
          onChange={e => setDescription(e.target.value)}
          placeholder="Description..."
          rows={2}
          className="mb-4 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-slate-500 focus:outline-none"
        />
      ) : skill.description ? (
        <p className="mb-4 text-sm text-slate-600">{skill.description}</p>
      ) : null}

      {/* Tabs */}
      <div className="mb-4 flex gap-1 rounded-lg bg-slate-100 p-1">
        {(['content', 'variables', 'config'] as const).map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`flex-1 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
              activeTab === tab ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            {tab === 'content' ? 'Content' : tab === 'variables' ? 'Variables' : 'Configuration'}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {activeTab === 'content' && (
        <div className="rounded-lg border border-slate-200 bg-white">
          {editing ? (
            <textarea
              value={content}
              onChange={e => setContent(e.target.value)}
              placeholder={skill.type === SkillType.PROMPT_TEMPLATE
                ? 'Enter your prompt template here...\nUse {{variable_name}} for dynamic values.'
                : 'Enter content...'}
              rows={16}
              className="w-full rounded-lg border-none bg-slate-900 p-4 font-mono text-sm text-slate-100 focus:outline-none"
            />
          ) : skill.content ? (
            <pre className="overflow-x-auto rounded-lg bg-slate-900 p-4 font-mono text-sm text-slate-100">
              {skill.content}
            </pre>
          ) : (
            <div className="p-8 text-center text-sm text-slate-400">No content defined</div>
          )}
        </div>
      )}

      {activeTab === 'variables' && (
        <div className="rounded-lg border border-slate-200 bg-white p-4">
          {editing ? (
            <div className="space-y-3">
              {variables.map((v, i) => (
                <div key={i} className="flex items-start gap-2 rounded-md border border-slate-200 p-3">
                  <div className="grid flex-1 grid-cols-2 gap-2">
                    <input
                      type="text"
                      placeholder="Name"
                      value={v.name}
                      onChange={e => updateVariable(i, 'name', e.target.value)}
                      className="rounded border border-slate-300 px-2 py-1 text-sm"
                    />
                    <input
                      type="text"
                      placeholder="Description"
                      value={v.description}
                      onChange={e => updateVariable(i, 'description', e.target.value)}
                      className="rounded border border-slate-300 px-2 py-1 text-sm"
                    />
                    <input
                      type="text"
                      placeholder="Default value"
                      value={v.defaultValue || ''}
                      onChange={e => updateVariable(i, 'defaultValue', e.target.value)}
                      className="rounded border border-slate-300 px-2 py-1 text-sm"
                    />
                    <label className="flex items-center gap-2 text-sm text-slate-600">
                      <input
                        type="checkbox"
                        checked={v.required}
                        onChange={e => updateVariable(i, 'required', e.target.checked)}
                        className="rounded border-slate-300"
                      />
                      Required
                    </label>
                  </div>
                  <button onClick={() => removeVariable(i)} className="text-slate-400 hover:text-red-500">
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ))}
              <button
                onClick={addVariable}
                className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700"
              >
                <Plus className="h-3.5 w-3.5" />
                Add Variable
              </button>
            </div>
          ) : skill.variables && skill.variables.length > 0 ? (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-left text-xs text-slate-500">
                  <th className="pb-2 font-medium">Name</th>
                  <th className="pb-2 font-medium">Description</th>
                  <th className="pb-2 font-medium">Default</th>
                  <th className="pb-2 font-medium">Required</th>
                </tr>
              </thead>
              <tbody>
                {skill.variables.map((v, i) => (
                  <tr key={i} className="border-b border-slate-100 last:border-0">
                    <td className="py-2 font-mono text-xs text-slate-900">{v.name}</td>
                    <td className="py-2 text-slate-600">{v.description}</td>
                    <td className="py-2 font-mono text-xs text-slate-400">{v.defaultValue || '-'}</td>
                    <td className="py-2">{v.required ? 'Yes' : 'No'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="py-8 text-center text-sm text-slate-400">No variables defined</div>
          )}
        </div>
      )}

      {activeTab === 'config' && (
        <div className="rounded-lg border border-slate-200 bg-white">
          {skill.configuration ? (
            <pre className="overflow-x-auto rounded-lg bg-slate-900 p-4 font-mono text-sm text-slate-100">
              {JSON.stringify(skill.configuration, null, 2)}
            </pre>
          ) : (
            <div className="p-8 text-center text-sm text-slate-400">No configuration defined</div>
          )}
        </div>
      )}
    </div>
  );
}
